import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import CustomButton from "../components/CustomButton";
import CalendarMonthGrid from "../components/Assessments/CalendarMonthGrid";
import MultiMonthCalendarGrid from "../components/Assessments/MultiMonthCalendarGrid";
import CalendarScheduledList from "../components/Assessments/CalendarScheduledList";
import CalendarTruncationNotice from "../components/Assessments/CalendarTruncationNotice";
import CalendarViewSpanToggle from "../components/Assessments/CalendarViewSpanToggle";
import CalendarPageSearch from "../components/Assessments/CalendarPageSearch";
import AssessmentCalendarFilters from "../components/Assessments/AssessmentCalendarFilters";
import TableExportMenu from "../components/Menus/TableExportMenu";
import {
  useGetAllAssessmentsForCalendar,
  useGetAuditorsListForFilters,
  useGetprovidersListForFilters,
  useGetSponsorListForFilters,
  UseGetRfiListForFilters,
} from "../custom-hooks/queries";
import { useNavigate } from "react-router-dom";
import { isBackendViewOnlyUser, useUserRole } from "../utils/helperMethods";
import { useSelector } from "react-redux";
import {
  calendarSubheading,
  canJoinCalendarAssessment,
  compareCalendarRows,
  dedupeCalendarBrowseRows,
  filterCalendarRows,
  parseScheduledDate,
  rowsInViewSpan,
  rowsOnDay,
  startOfDay,
} from "../utils/assessmentCalendarRules";
import {
  buildCalendarDetailsTarget,
  findNextCalendarAssessment,
  isCalendarJoinOnlyRow,
} from "../utils/assessmentCalendarNavigation";
import CalendarJoinRequestFlow from "../components/Assessments/CalendarJoinRequestFlow";
import {
  buildCalendarExportRecords,
  CALENDAR_EXPORT_HEADERS,
} from "../utils/assessmentCalendarExport";
import { formatCalendarShortDate } from "../utils/calendarRowDisplay";
import { formatCalendarRangeLabel } from "../utils/calendarMultiMonth";
import CalendarMonthYearPicker from "../components/Assessments/CalendarMonthYearPicker";
import {
  buildCalendarFilterSections,
  buildDefaultAdminCalendarStatusFilters,
  mapCalendarFiltersToApi,
} from "../utils/calendarFilterSections";

const NAVIGATION_MONTH_SPAN = 24;

const AssessmentCalendar = () => {
  const navigate = useNavigate();
  const userRole = useUserRole();
  const isAdmin = isBackendViewOnlyUser(userRole);
  const hideRequestActions = isAdmin;
  const adminStatusDefaultsApplied = useRef(false);
  const { data: providers } = useGetprovidersListForFilters();
  const { data: sponsors } = useGetSponsorListForFilters();
  const { data: rfis } = UseGetRfiListForFilters();
  const { data: auditors } = useGetAuditorsListForFilters();
  const today = useMemo(() => startOfDay(new Date()), []);

  const [selectedFilters, setSelectedFilters] = useState({});

  useEffect(() => {
    if (!isAdmin || adminStatusDefaultsApplied.current) return;
    adminStatusDefaultsApplied.current = true;
    setSelectedFilters((prev) => {
      if (prev.Status?.length) return prev;
      return { ...prev, Status: buildDefaultAdminCalendarStatusFilters() };
    });
  }, [isAdmin]);
  const [calendarViewSpan, setCalendarViewSpan] = useState(1);
  const [monthDate, setMonthDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(today);

  const searchQuery = useSelector(
    (state) => state.providersFilters.searchTextByPage["assessmentCalendar"]
  );

  const filterSections = useMemo(
    () =>
      buildCalendarFilterSections({
        isAdmin,
        providers: providers || [],
        sponsors: sponsors || [],
        rfis: rfis || [],
        auditors: auditors || [],
      }),
    [isAdmin, providers, sponsors, rfis, auditors]
  );

  const apiFilters = useMemo(
    () => mapCalendarFiltersToApi(selectedFilters, { isAdmin }),
    [selectedFilters, isAdmin]
  );

  const currentMonth = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}-01`;
  const navigationMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;

  const { data: requestList, isLoading, refetch: refetchCalendar } = useGetAllAssessmentsForCalendar(
    1,
    apiFilters.auditorIds,
    apiFilters.providerIds,
    apiFilters.vqaStatus,
    apiFilters.rfiIds,
    null,
    currentMonth,
    null,
    null,
    searchQuery,
    apiFilters.sponsorIds,
    calendarViewSpan,
    apiFilters
  );

  const { data: navigationList } = useGetAllAssessmentsForCalendar(
    1,
    apiFilters.auditorIds,
    apiFilters.providerIds,
    apiFilters.vqaStatus,
    apiFilters.rfiIds,
    null,
    navigationMonth,
    null,
    null,
    searchQuery,
    apiFilters.sponsorIds,
    NAVIGATION_MONTH_SPAN,
    apiFilters
  );

  // Server already applies calendar filters; only drop rows the grid rules hide.
  const visibleRows = useMemo(
    () => dedupeCalendarBrowseRows(filterCalendarRows(requestList?.items || [])),
    [requestList]
  );
  const navigationRows = useMemo(
    () => filterCalendarRows(navigationList?.items || []),
    [navigationList]
  );
  const sidebarRows = rowsInViewSpan(visibleRows, monthDate, calendarViewSpan);
  const selectedDayRows = rowsOnDay(visibleRows, selectedDate);

  const rangeLabel = formatCalendarRangeLabel(
    monthDate.getFullYear(),
    monthDate.getMonth(),
    calendarViewSpan
  );

  const shiftMonth = (delta) => {
    const next = new Date(
      monthDate.getFullYear(),
      monthDate.getMonth() + delta * calendarViewSpan,
      1
    );
    setMonthDate(next);
    if (calendarViewSpan === 1) {
      setSelectedDate(next);
    }
  };

  const goToToday = () => {
    const now = startOfDay(new Date());
    setMonthDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(now);
  };

  const goToNextAssessment = () => {
    const next = findNextCalendarAssessment(navigationRows, { selectedDate, today });
    if (!next?.date) return;
    setMonthDate(new Date(next.date.getFullYear(), next.date.getMonth(), 1));
    setSelectedDate(startOfDay(next.date));
  };

  const [joinRow, setJoinRow] = useState(null);

  const openRow = (row) => {
    if (isCalendarJoinOnlyRow(row, { isAdmin })) {
      if (canJoinCalendarAssessment(row, today)) setJoinRow(row);
      return;
    }
    const target = buildCalendarDetailsTarget(row, { isAdmin });
    navigate(target.pathname, { state: target.state });
  };

  const nextAfterSelected = useMemo(() => {
    const selectedStart = startOfDay(selectedDate).getTime();
    return [...visibleRows]
      .map((row) => ({ row, date: parseScheduledDate(row.assessment_start_date) }))
      .filter((item) => item.date && item.date.getTime() > selectedStart)
      .sort((a, b) => compareCalendarRows(a.row, b.row))[0];
  }, [visibleRows, selectedDate]);

  return (
    <div className="min-h-screen w-full px-4 pb-16 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4 py-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Assessment Calendar</h1>
          <p className="mt-1 text-sm text-gray-500">{calendarSubheading(isAdmin)}</p>
        </div>
        <TableExportMenu
          getData={() => ({
            records: buildCalendarExportRecords(visibleRows, { isAdmin, today }),
            headers: CALENDAR_EXPORT_HEADERS,
          })}
          reportTitle="Assessment Calendar"
          reportSubtitle={calendarSubheading(isAdmin)}
          filePrefix="Assessment_Calendar"
          downloadType="Assessment Calendar"
        />
      </header>

      <div className="space-y-8 overflow-visible py-2">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <CalendarPageSearch />
            <AssessmentCalendarFilters
              sections={filterSections}
              selectedFilters={selectedFilters}
              onFilterChange={setSelectedFilters}
            />
          </div>
        </div>

        <div className="overflow-visible rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="p-6 lg:grid lg:grid-cols-12 lg:gap-x-0">
            <div className="border-gray-200 pr-0 text-left lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:border-r lg:pr-6 xl:col-span-6">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold leading-6 text-gray-900">
                    Scheduled Assessments
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">{rangeLabel}</p>
                  {calendarViewSpan > 1 ? (
                    <p className="mt-0.5 text-xs text-gray-400">
                      {sidebarRows.length} scheduled in this range
                    </p>
                  ) : null}
                </div>
                <CalendarViewSpanToggle value={calendarViewSpan} onChange={setCalendarViewSpan} />
              </div>
              <CalendarTruncationNotice data={requestList} />
              {isLoading ? (
                <p className="py-12 text-sm text-gray-500">Loading assessments...</p>
              ) : (
                <CalendarScheduledList
                  rows={sidebarRows}
                  isAdmin={isAdmin}
                  today={today}
                  onOpen={openRow}
                  onRequestToJoin={setJoinRow}
                  onRefetch={refetchCalendar}
                />
              )}
              {!hideRequestActions ? (
                <div className="mt-6 border-t border-gray-100 pt-4">
                  <CustomButton
                    color="bg-indigo-600"
                    textColor="text-white"
                    width="w-full sm:w-auto"
                    text="Request Assessment"
                    onClick={() => navigate("/Assessment/Create")}
                    height="h-10"
                  />
                </div>
              ) : null}
            </div>

            <div className="mt-8 pl-0 lg:col-span-7 lg:col-start-6 lg:row-start-1 lg:mt-0 lg:pl-6 xl:col-span-6 xl:col-start-7">
              <div className="mb-4 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={goToToday}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={goToNextAssessment}
                  className="rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Next Assessment
                </button>
              </div>

              <div className="flex items-center text-gray-900">
                <button
                  type="button"
                  onClick={() => shiftMonth(-1)}
                  className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">
                    {calendarViewSpan > 1 ? "Previous period" : "Previous month"}
                  </span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                <CalendarMonthYearPicker
                  monthDate={monthDate}
                  calendarViewSpan={calendarViewSpan}
                  rangeLabel={rangeLabel}
                  onNavigate={(year, month) => {
                    const next = new Date(year, month, 1);
                    setMonthDate(next);
                    if (calendarViewSpan === 1) setSelectedDate(next);
                  }}
                />
                <button
                  type="button"
                  onClick={() => shiftMonth(1)}
                  className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">
                    {calendarViewSpan > 1 ? "Next period" : "Next month"}
                  </span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              {!isLoading && calendarViewSpan === 1 ? (
                <div className="relative z-20 mt-4">
                  <CalendarMonthGrid
                    monthDate={monthDate}
                    rows={visibleRows}
                    isAdmin={isAdmin}
                    today={today}
                    selectedDate={selectedDate}
                    onSelectDate={setSelectedDate}
                  />
                </div>
              ) : null}
              {!isLoading && calendarViewSpan > 1 ? (
                <div className="relative z-20">
                <MultiMonthCalendarGrid
                  monthDate={monthDate}
                  calendarViewSpan={calendarViewSpan}
                  rows={visibleRows}
                  isAdmin={isAdmin}
                  today={today}
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
                </div>
              ) : null}

              {selectedDayRows.length === 0 ? (
                <div className="relative z-0 mt-6 rounded-lg border border-gray-200 bg-white p-4 text-left text-sm text-gray-600">
                  <p>There are no scheduled assessments for this day.</p>
                  {nextAfterSelected?.date ? (
                    <p className="mt-2">
                      The next scheduled assessment is on{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setMonthDate(
                            new Date(
                              nextAfterSelected.date.getFullYear(),
                              nextAfterSelected.date.getMonth(),
                              1
                            )
                          );
                          setSelectedDate(nextAfterSelected.date);
                        }}
                        className="font-medium text-indigo-600 underline-offset-2 hover:text-indigo-900 hover:underline"
                      >
                        {formatCalendarShortDate(nextAfterSelected.date)}
                      </button>{"."}
                    </p>
                  ) : (
                    <p className="mt-2 text-gray-500">No upcoming scheduled assessments found.</p>
                  )}
                </div>
              ) : (
                <div className="relative z-0 mt-6 space-y-4 text-left">
                  <h3 className="text-sm font-semibold text-gray-700">
                    {selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </h3>
                  <CalendarScheduledList
                    rows={selectedDayRows}
                    isAdmin={isAdmin}
                    today={today}
                    onOpen={openRow}
                    onRequestToJoin={setJoinRow}
                    onRefetch={refetchCalendar}
                    compact
                    emptyMessage="There are no scheduled assessments for this day."
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <CalendarJoinRequestFlow row={joinRow} onClose={() => setJoinRow(null)} onJoined={refetchCalendar} />
    </div>
  );
};

export default AssessmentCalendar;
